import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scissors, Clock, ChevronRight, Check, MapPin } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { servicesAPI, barbersAPI, mediaAPI } from '../lib/api';

const ServicesPage = () => {
    const [services, setServices] = useState([]);
    const [barbers, setBarbers] = useState([]);
    const [mediaSettings, setMediaSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedBranch, setSelectedBranch] = useState('swieqi');

    useEffect(() => {
        const loadServices = async () => {
            try {
                const [servicesResponse, barbersResponse, mediaResponse] = await Promise.all([
                    servicesAPI.getAll(),
                    barbersAPI.getAll(),
                    mediaAPI.getSettings()
                ]);
                setServices(servicesResponse.data);
                setBarbers(barbersResponse.data);
                setMediaSettings(mediaResponse?.data || null);
            } catch (error) {
                console.error('Failed to load services:', error);
            } finally {
                setLoading(false);
            }
        };
        loadServices();
    }, []);

    const resolveMediaUrl = (value) => {
        if (!value) return '';
        if (value.startsWith('http')) return value;
        return `${process.env.REACT_APP_BACKEND_URL}${value}`;
    };

    const staffBranchMap = barbers.reduce((acc, barber) => {
        const name = (barber?.name || '').toLowerCase();
        if (name.includes('swieqi')) {
            acc[barber.key] = 'swieqi';
        }
        if (name.includes('san gwann') || name.includes('san ġwann') || name.includes('sangwann')) {
            acc[barber.key] = 'san_gwann';
        }
        return acc;
    }, {});

    const getServiceBranches = (service) => {
        const branches = new Set();
        const name = (service?.name || '').toLowerCase();

        if (name.includes('swieqi')) {
            branches.add('swieqi');
        }
        if (name.includes('san gwann') || name.includes('san ġwann') || name.includes('sangwann')) {
            branches.add('san_gwann');
        }

        if (service?.staff_keys?.length) {
            service.staff_keys.forEach((key) => {
                const branch = staffBranchMap[key];
                if (branch) {
                    branches.add(branch);
                }
            });
        }

        if (branches.size === 0) {
            return ['swieqi', 'san_gwann'];
        }

        return Array.from(branches);
    };

    const serviceImages = mediaSettings?.service_images || {};

    // Filter services by branch (based on staff assignments + service naming)
    const filteredServices = selectedBranch 
        ? services.filter((service) => getServiceBranches(service).includes(selectedBranch))
        : services;

    // Count services per branch
    const swieqiCount = services.filter((service) => getServiceBranches(service).includes('swieqi')).length;
    const sanGwannCount = services.filter((service) => getServiceBranches(service).includes('san_gwann')).length;

    // Branch Selection View
    if (!selectedBranch) {
        return (
            <div className="min-h-screen bg-dark">
                <Navbar />

                {/* Hero */}
                <section className="pt-32 pb-16 px-6 md:px-12">
                    <div className="max-w-7xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="text-accent-pink font-urbanist font-semibold text-sm mb-2 block">OUR SERVICES</span>
                            <h1 className="font-anton text-5xl md:text-6xl mb-4">CHOOSE YOUR LOCATION</h1>
                            <p className="font-urbanist text-xl text-zinc-400 max-w-2xl mx-auto">
                                Select your preferred branch to view available services and pricing
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Branch Selection Cards */}
                <section className="px-6 md:px-12 pb-24">
                    <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
                        {/* Swieqi Branch */}
                        <motion.button
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            onClick={() => setSelectedBranch('swieqi')}
                            className="group relative overflow-hidden border-2 border-zinc-800 hover:border-primary transition-all p-8 text-left"
                            data-testid="branch-select-swieqi"
                        >
                            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <MapPin className="text-primary mb-4" size={40} />
                            <h2 className="font-anton text-4xl mb-2 group-hover:text-primary transition-colors">SWIEQI</h2>
                            <p className="text-zinc-400 font-urbanist mb-4">Triq is-Swieqi 4</p>
                            <p className="text-zinc-500 font-urbanist text-sm mb-6">
                                Barbers: <span className="text-white">Riccardo & Abde</span>
                            </p>
                            <div className="flex items-center gap-2 text-primary font-urbanist font-semibold">
                                View Services <ChevronRight size={18} />
                            </div>
                        </motion.button>
                    </div>
                </section>
            </div>
        );
    }

    // Services List View
    const accentColor = selectedBranch === 'swieqi' ? 'primary' : 'accent-cyan';
    const branchName = selectedBranch === 'swieqi' ? 'SWIEQI' : 'SAN ĠWANN';

    return (
        <div className="min-h-screen bg-dark">
            <Navbar />

            {/* Hero */}
            <section className="pt-32 pb-8 px-6 md:px-12" data-testid="services-hero">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="flex items-center gap-4 mb-4">
                            <MapPin className={`text-${accentColor}`} size={32} />
                            <h1 className="font-anton text-5xl md:text-6xl">{branchName}</h1>
                        </div>
                        <p className="font-urbanist text-xl text-zinc-400 max-w-2xl">
                            Browse our services and book your appointment
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Setmore Sync Badge */}
            <section className="px-6 md:px-12 mb-8">
                <div className="max-w-7xl mx-auto">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 bg-${accentColor}/10 border border-${accentColor}/30 text-${accentColor} text-sm font-urbanist`}>
                        <Check size={16} />
                        Synced with Setmore - Real-time availability
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="px-6 md:px-12 pb-24" data-testid="services-grid">
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="bg-dark-paper border border-zinc-800 p-6 animate-pulse">
                                    <div className="h-4 bg-zinc-800 rounded w-1/4 mb-4" />
                                    <div className="h-6 bg-zinc-800 rounded w-3/4 mb-2" />
                                    <div className="h-4 bg-zinc-800 rounded w-1/2" />
                                </div>
                            ))}
                        </div>
                    ) : filteredServices.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-zinc-500 font-urbanist text-lg">No services found for this location.</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredServices.map((service, i) => (
                                <motion.div
                                    key={service.key}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className={`service-card bg-dark-paper border border-zinc-800 p-6 hover:border-${accentColor} transition-all group`}
                                    data-testid={`service-item-${service.key}`}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        {serviceImages[service.key] ? (
                                            <img
                                                src={resolveMediaUrl(serviceImages[service.key])}
                                                alt={service.name}
                                                className="w-16 h-16 object-cover border border-zinc-800"
                                                data-testid={`service-image-${service.key}`}
                                            />
                                        ) : (
                                            <Scissors className={`text-zinc-600 group-hover:text-${accentColor} transition-colors`} size={24} />
                                        )}
                                        <span className={`font-anton text-2xl text-${accentColor}`}>
                                            {service.currency === 'EUR' ? '€' : service.currency}
                                            {service.price}
                                        </span>
                                    </div>
                                    <h3 className={`font-anton text-xl text-white mb-2 group-hover:text-${accentColor} transition-colors`}>
                                        {service.name}
                                    </h3>
                                    <div className="flex items-center gap-4 text-zinc-500 font-urbanist text-sm">
                                        <span className="flex items-center gap-1">
                                            <Clock size={14} />
                                            {service.duration} min
                                        </span>
                                    </div>
                                    {service.description && (
                                        <p className="text-zinc-600 text-sm mt-3 font-urbanist">
                                            {service.description}
                                        </p>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-6 md:px-12 bg-dark-paper border-t border-zinc-800" data-testid="services-cta">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-anton text-3xl md:text-4xl mb-4">READY TO BOOK?</h2>
                    <p className="font-urbanist text-zinc-400 mb-8">
                        Book your appointment now and we'll take care of the rest.
                    </p>
                    <Link to="/book" data-testid="services-book-btn">
                        <Button className={`bg-${accentColor} text-black font-anton tracking-wider px-8 py-6 hover:bg-white neon-glow shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none`}>
                            <Scissors className="mr-2" />
                            BOOK NOW
                            <ChevronRight className="ml-2" />
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default ServicesPage;
